/* Upload queue core (row #40, 2026-10-19), shared by the page
 * (src/field/outbox.ts, bundled) and the service worker (sw.js, through
 * importScripts, for Background Sync). A classic script with no DOM use: it
 * sets globalThis.WrapOutboxCore.
 *
 * Why (row #40): on 15 Oct an iPhone's scan frames stopped uploading the
 * moment the app went to the background, and 17 of them sat on the phone for
 * days. The old drain() had no timeout, so a request frozen across a
 * suspension never settled, `draining` stayed true, and every later retry
 * (timer, online, foreground, "Upload now") returned at once until the page
 * was reloaded. Now every request has a timeout, the page can abort a run
 * that spans a suspension, and Android's Background Sync can drain the queue
 * with the app closed (iOS has no Background Sync: there the queue drains
 * only while the app is open, so the page shows what is waiting).
 *
 * Outcomes per item:
 *   done    2xx: removed from the queue
 *   retry   offline, timed out, signed out (401), 408, 429, 5xx: the whole
 *           queue stops and tries again later (the next item would fail too)
 *   wait    only this item waits (an MSQA submit whose photos are still on
 *           their way, 409 photos_pending); the rest carry on, except later
 *           items of the same group (a scan session's frames keep their order)
 *   failed  any other 4xx: kept as "needs attention" until the user discards it
 * The server is idempotent on client_uuid, so a retry after a lost response
 * never duplicates.
 */
(function (root) {
  "use strict";

  var LOCK = "wrap-outbox";

  function isScan(it) {
    return typeof it.kind === "string" && it.kind.indexOf("scan_") === 0;
  }

  /** Oldest first; captures and MSQA items before AR scan items (a long scan
   * backlog never holds back a crew's capture). */
  function order(items) {
    var all = items.slice().sort(function (a, b) { return a.created - b.created; });
    return all.filter(function (i) { return !isScan(i); }).concat(all.filter(isScan));
  }

  function itemBytes(it) {
    var n = 0;
    (it.photos || []).forEach(function (b) { n += (b && b.size) || 0; });
    if (it.json !== undefined) n += JSON.stringify(it.json).length;
    return n;
  }

  /** 45 s plus 1 s per 25 kB (a slow mobile link), at most 10 min. */
  function timeoutMs(it) {
    return Math.min(600000, 45000 + Math.ceil(itemBytes(it) / 25000) * 1000);
  }

  function request(it) {
    if (it.endpoint) {
      return { url: it.endpoint, init: { method: it.method || "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(it.json === undefined ? {} : it.json) } };
    }
    var form = new FormData();
    form.append("meta", JSON.stringify(it.meta));
    if (it.upload_endpoint) {
      var field = it.file_field || "file";
      (it.photos || []).forEach(function (b, i) { form.append(field, b, field + "-" + (i + 1) + ".jpg"); });
      return { url: it.upload_endpoint, init: { method: "POST", body: form, credentials: "same-origin" } };
    }
    form.append("photo_headings", JSON.stringify(it.photo_headings || []));
    (it.photos || []).forEach(function (b, i) { form.append("photos", b, "photo-" + (i + 1) + ".jpg"); });
    return { url: "/api/field/captures", init: { method: "POST", body: form, credentials: "same-origin" } };
  }

  /** A non-2xx answer: {result, error}. */
  function classify(status, detail, it) {
    var error = status === 401 ? "Signed out - sign in again to upload" : detail || String(status);
    if (status === 401 || status === 408 || status === 429 || status >= 500) return { result: "retry", error: error };
    if (it.endpoint && status === 409 && String(detail).indexOf("photos_pending") === 0) return { result: "wait", error: error };
    return { result: "failed", error: error };
  }

  async function readDetail(res) {
    try {
      var body = await res.json();
      if (body && typeof body.detail === "string") return body.detail;
    } catch (e) {
      /* not JSON */
    }
    return String(res.status);
  }

  /** One upload: {result, error, record}. `outer` aborts it from outside. */
  async function send(it, fetchFn, outer, setTimer, clearTimer) {
    var ctrl = new AbortController();
    var timedOut = false;
    var onOuter = function () { ctrl.abort(); };
    if (outer) {
      if (outer.aborted) return { result: "aborted", error: null };
      outer.addEventListener("abort", onOuter);
    }
    var timer = setTimer(function () { timedOut = true; ctrl.abort(); }, timeoutMs(it));
    try {
      var rq = request(it);
      rq.init.signal = ctrl.signal;
      var res = await fetchFn(rq.url, rq.init);
      if (res.ok) {
        var rec = null;
        try { rec = await res.json(); } catch (e) { rec = null; }
        return { result: "done", error: null, record: rec };
      }
      var c = classify(res.status, await readDetail(res), it);
      return { result: c.result, error: c.error };
    } catch (e) {
      if (timedOut) return { result: "retry", error: "Upload timed out - will retry" };
      if (outer && outer.aborted) return { result: "aborted", error: null };
      return { result: "retry", error: "No connection - will retry" };
    } finally {
      clearTimer(timer);
      if (outer) outer.removeEventListener("abort", onOuter);
    }
  }

  /** Upload everything queued, one at a time. deps:
   *   list(), put(item), del(client_uuid)  the queue (IndexedDB)
   *   fetch                                 the network
   *   owner        the signed-in username (null: unknown)
   *   strictOwner  true in the service worker: with no owner known, no
   *                owned item is sent (the page, as before, sends everything
   *                while it doesn't know the user yet)
   *   signal       aborts the run (the page, on resume after a suspension)
   *   onUploaded(record, item), onChange()  optional
   *   now(), setTimeout, clearTimeout       optional (tests)
   * Returns {sent, rejected, waiting, stopped}: `stopped` is the error that
   * stopped the queue (null: it ran to the end), or "aborted". */
  async function drainOnce(deps) {
    var now = deps.now || Date.now;
    var setTimer = deps.setTimeout || setTimeout;
    var clearTimer = deps.clearTimeout || clearTimeout;
    var out = { sent: 0, rejected: 0, waiting: 0, stopped: null };
    var held = {};
    var items = order(await deps.list());
    for (var k = 0; k < items.length; k++) {
      var it = items[k];
      if (deps.signal && deps.signal.aborted) { out.stopped = "aborted"; break; }
      if (it.failed) continue;
      if (it.owner && (deps.owner || deps.strictOwner) && it.owner !== deps.owner) continue; // someone else's capture
      if (it.group && held[it.group]) { out.waiting += 1; continue; }
      it.attempts = (it.attempts || 0) + 1;
      it.last_attempt_at = now();
      var r = await send(it, deps.fetch, deps.signal, setTimer, clearTimer);
      if (r.result === "aborted") { out.stopped = "aborted"; break; }
      if (r.result === "done") {
        await deps.del(it.client_uuid);
        out.sent += 1;
        if (deps.onUploaded) deps.onUploaded(r.record, it);
      } else {
        it.last_error = r.error;
        it.failed = r.result === "failed";
        await deps.put(it);
        if (r.result === "failed") out.rejected += 1;
        if (r.result === "wait") {
          out.waiting += 1;
          if (it.group) held[it.group] = true;
        }
        if (r.result === "retry") { out.stopped = r.error; if (deps.onChange) deps.onChange(); break; }
      }
      if (deps.onChange) deps.onChange();
    }
    return out;
  }

  /** Run fn under the cross-context lock (page and service worker never drain
   * at the same time). Returns {busy: true} when another context holds it. */
  function withLock(locks, fn) {
    if (!locks || typeof locks.request !== "function") return fn();
    return locks.request(LOCK, { ifAvailable: true }, function (lock) {
      return lock ? fn() : { sent: 0, rejected: 0, waiting: 0, stopped: null, busy: true };
    });
  }

  root.WrapOutboxCore = { order: order, timeoutMs: timeoutMs, classify: classify, request: request, drainOnce: drainOnce, withLock: withLock,
    SYNC_TAG: "wrap-outbox" };
})(typeof globalThis !== "undefined" ? globalThis : self);
